import { z } from 'zod';
import { companyIdSchema } from './company';

const isoDateTimeSchema = z.string().datetime({
  error: 'La date doit être au format ISO 8601.',
});

const nonNegativeIntSchema = z.number().int().nonnegative();
const nonNegativeNumberSchema = z.number().nonnegative();
const nullableNonNegativeIntSchema = nonNegativeIntSchema.nullable();

export const usageTypeSchema = z.enum(['chat_completion', 'document_indexing', 'document_retrieval'], {
  error: 'Le type d usage est invalide.',
});

export const usageSourceSchema = z.enum(['chat', 'embedding', 'pageindex', 'web'], {
  error: 'La source d usage est invalide.',
});

export const usageMeasurementSourceSchema = z.enum(['estimated', 'provider_reported'], {
  error: 'La source de mesure est invalide.',
});

export const usageStatusSchema = z.enum(['error', 'success'], {
  error: 'Le statut d usage est invalide.',
});

export const usageOperationSchema = z.enum([
  'chat.attachment_extraction',
  'chat.attachment_upload',
  'chat.message_completion',
  'chat.route_classification',
  'chat.title_generation',
  'chat.web_answer_synthesis',
  'chat.web_research_deep',
  'chat.web_research_planning',
  'chat.web_tool_loop',
  'docgen.content_planning',
  'docgen.execution',
  'docgen.script_correction',
  'docgen.script_generation',
  'docgen.visual_review',
  'mistral.embedding',
  'pageindex.document_indexing_llm',
  'pageindex.document_text_count',
  'pageindex.retrieval_agent',
  'pageindex.retrieval_document_structure',
  'pageindex.retrieval_page_content',
  'pageindex.tree_extraction',
  'rag.cache_hit_retrieval',
  'rag.indexing_total',
  'rag.pageindex_tool',
  'rag.query_rewrite',
  'rag.self_reflection',
  'rag.table_classification',
  'selfhosted.embedding',
  'unknown',
  'web.fetch',
  'web.search',
], {
  error: 'L operation d usage est invalide.',
});

export const usageTotalsSchema = z.object({
  estimatedTokens: nonNegativeIntSchema,
  inputTokens: nonNegativeIntSchema,
  outputTokens: nonNegativeIntSchema,
  providerReportedTokens: nonNegativeIntSchema,
  totalTokens: nonNegativeIntSchema,
});

export const usageBreakdownItemSchema = z.object({
  key: z.string().trim().min(1),
  totals: usageTotalsSchema,
});

export const usageLogSchema = z.object({
  cachedInputTokens: nullableNonNegativeIntSchema,
  completionTokens: nullableNonNegativeIntSchema,
  companyId: companyIdSchema,
  conversationId: z.uuid({ error: 'L identifiant de conversation est invalide.' }).nullable(),
  costEur: nonNegativeNumberSchema.nullable(),
  createdAt: isoDateTimeSchema,
  documentId: z.uuid({ error: 'L identifiant de document est invalide.' }).nullable(),
  durationMs: nullableNonNegativeIntSchema,
  externalId: z.string().trim().min(1).nullable(),
  id: z.uuid({
    error: 'L identifiant du log d usage est invalide.',
  }),
  measurementSource: usageMeasurementSourceSchema,
  messageId: z.uuid({ error: 'L identifiant de message est invalide.' }).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  model: z.string().nullable(),
  operation: usageOperationSchema,
  pricingSource: z.string().nullable(),
  pricingVersion: z.string().nullable(),
  promptTokens: nullableNonNegativeIntSchema,
  provider: z.string().nullable(),
  reasoningTokens: nullableNonNegativeIntSchema,
  source: usageSourceSchema,
  status: usageStatusSchema,
  totalTokens: nullableNonNegativeIntSchema,
  type: usageTypeSchema,
  userId: z.string().trim().min(1).nullable(),
});

export const usageSummarySchema = z.object({
  conversationCount: nonNegativeIntSchema,
  eventsCount: nonNegativeIntSchema,
  messageCount: nonNegativeIntSchema,
  byOperation: z.array(usageBreakdownItemSchema),
  byType: z.array(usageBreakdownItemSchema),
  costEur: nonNegativeNumberSchema,
  totals: usageTotalsSchema,
  userId: z.string().trim().min(1),
  webSearchCount: nonNegativeIntSchema,
});

export const globalUsageSummarySchema = z.object({
  conversationCount: nonNegativeIntSchema,
  eventsCount: nonNegativeIntSchema,
  messageCount: nonNegativeIntSchema,
  byOperation: z.array(usageBreakdownItemSchema),
  byType: z.array(usageBreakdownItemSchema),
  costEur: nonNegativeNumberSchema,
  totals: usageTotalsSchema,
  userCount: nonNegativeIntSchema,
});

export const companyUsageSummarySchema = z.object({
  byOperation: z.array(usageBreakdownItemSchema),
  byType: z.array(usageBreakdownItemSchema),
  companyId: companyIdSchema,
  conversationCount: nonNegativeIntSchema,
  eventsCount: nonNegativeIntSchema,
  memberCount: nonNegativeIntSchema,
  messageCount: nonNegativeIntSchema,
  totals: usageTotalsSchema,
  webSearchCount: nonNegativeIntSchema,
});

export const conversationUsageSchema = z.object({
  conversationId: z.uuid({
    error: 'L identifiant de conversation est invalide.',
  }),
  costEur: nonNegativeNumberSchema,
  lastEventAt: isoDateTimeSchema.nullable(),
  messageCount: nonNegativeIntSchema,
  title: z.string().nullable(),
  totals: usageTotalsSchema,
  userId: z.string().trim().min(1),
});

export const userUsageOverviewSchema = z.object({
  costEur: nonNegativeNumberSchema,
  lastEventAt: isoDateTimeSchema.nullable(),
  totals: usageTotalsSchema,
  userId: z.string().trim().min(1),
});

const usageCostBySourceItemSchema = z.object({
  costEur: nonNegativeNumberSchema,
  eventsCount: nonNegativeIntSchema,
});

export const usageCostSummarySchema = z.object({
  bySource: z.object({
    chat: usageCostBySourceItemSchema,
    embedding: usageCostBySourceItemSchema,
    pageindex: usageCostBySourceItemSchema,
    web: usageCostBySourceItemSchema,
  }),
  current: z.object({
    costEur: nonNegativeNumberSchema,
    estimatedTokens: nonNegativeIntSchema,
    eventsCount: nonNegativeIntSchema,
    inputTokens: nonNegativeIntSchema,
    outputTokens: nonNegativeIntSchema,
    providerReportedTokens: nonNegativeIntSchema,
  }),
  deltaCostPct: z.number().nullable(),
  deltaTokensPct: z.number().nullable(),
  previous: z.object({
    costEur: nonNegativeNumberSchema,
    estimatedTokens: nonNegativeIntSchema,
    eventsCount: nonNegativeIntSchema,
    inputTokens: nonNegativeIntSchema,
    outputTokens: nonNegativeIntSchema,
    providerReportedTokens: nonNegativeIntSchema,
  }),
});

export const usageCostTimeSeriesGranularitySchema = z.enum(['hour', 'day', 'week']);

export const usageCostTimeSeriesBucketSchema = z.object({
  bucketStart: isoDateTimeSchema,
  costEur: nonNegativeNumberSchema,
  costEurBySource: z.object({
    chat: nonNegativeNumberSchema,
    embedding: nonNegativeNumberSchema,
    pageindex: nonNegativeNumberSchema,
    web: nonNegativeNumberSchema,
  }),
  inputTokens: nonNegativeIntSchema,
  outputTokens: nonNegativeIntSchema,
});

export const usageCostTimeSeriesSchema = z.object({
  buckets: z.array(usageCostTimeSeriesBucketSchema),
  granularity: usageCostTimeSeriesGranularitySchema,
  rangeEnd: isoDateTimeSchema,
  rangeStart: isoDateTimeSchema,
});

export const companyUsageTimeseriesBucketSchema = z.object({
  bucketStart: isoDateTimeSchema,
  totals: usageTotalsSchema,
});

export const companyUsageTimeseriesSchema = z.object({
  buckets: z.array(companyUsageTimeseriesBucketSchema),
  granularity: usageCostTimeSeriesGranularitySchema,
  rangeEnd: isoDateTimeSchema,
  rangeStart: isoDateTimeSchema,
});

export const usageCostByModelItemSchema = z.object({
  costEur: nonNegativeNumberSchema,
  eventsCount: nonNegativeIntSchema,
  inputTokens: nonNegativeIntSchema,
  model: z.string(),
  outputTokens: nonNegativeIntSchema,
  pricingSource: z.string(),
  provider: z.string(),
});

export const usageCostByModelSchema = z.object({
  items: z.array(usageCostByModelItemSchema),
});

export const companyUsageByModelItemSchema = z.object({
  eventsCount: nonNegativeIntSchema,
  model: z.string().trim().min(1),
  provider: z.string().trim().min(1),
  totals: usageTotalsSchema,
});

export const companyUsageByModelSchema = z.object({
  items: z.array(companyUsageByModelItemSchema),
});

const companyUsagePeriodQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).optional(),
});

const companyUsagePaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(20),
});

export const companyUsageSummaryQuerySchema = companyUsagePeriodQuerySchema;

export const companyUsageTopConversationsQuerySchema = companyUsagePeriodQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

export const companyUsageMembersQuerySchema = companyUsagePaginationQuerySchema.extend({
  days: z.coerce.number().int().min(1).max(365).optional(),
  search: z.string().trim().max(200).optional(),
  sortBy: z.enum(['eventsCount', 'lastEventAt', 'name', 'totalTokens'], {
    error: 'Le critère de tri des membres est invalide.',
  }).default('totalTokens'),
  sortDirection: z.enum(['asc', 'desc'], {
    error: 'Le sens de tri est invalide.',
  }).default('desc'),
});

export const companyUsageEventsQuerySchema = companyUsagePaginationQuerySchema.extend({
  conversationId: z.uuid({
    error: 'L’identifiant de conversation est invalide.',
  }).optional(),
  days: z.coerce.number().int().min(1).max(365).optional(),
  documentId: z.uuid({
    error: 'L’identifiant de document est invalide.',
  }).optional(),
  measurementSource: usageMeasurementSourceSchema.optional(),
  operation: usageOperationSchema.optional(),
  source: usageSourceSchema.optional(),
  status: usageStatusSchema.optional(),
  type: usageTypeSchema.optional(),
  userId: z.string().trim().min(1).optional(),
});

export const companyUsageMemberParamsSchema = z.object({
  userId: z.string().trim().min(1, {
    error: 'L’identifiant utilisateur est requis.',
  }),
});

export const usagePricingModelStatusSchema = z.enum(['active', 'inactive']);

export const usagePricingModelSchema = z.object({
  cachedInputEurPerMillion: nonNegativeNumberSchema.nullable(),
  id: z.uuid({ error: 'L identifiant de pricing est invalide.' }),
  inputEurPerMillion: nonNegativeNumberSchema,
  model: z.string().trim().min(1),
  outputEurPerMillion: nonNegativeNumberSchema,
  provider: z.string().trim().min(1),
  reasoningOutputEurPerMillion: nonNegativeNumberSchema.nullable(),
  status: usagePricingModelStatusSchema,
  updatedAt: isoDateTimeSchema,
});

export const usagePricingModelsSchema = z.object({
  items: z.array(usagePricingModelSchema),
});

export const usagePricingModelUpsertSchema = z.object({
  cachedInputEurPerMillion: nonNegativeNumberSchema.nullable().optional(),
  inputEurPerMillion: nonNegativeNumberSchema,
  model: z.string().trim().min(1),
  outputEurPerMillion: nonNegativeNumberSchema,
  provider: z.string().trim().min(1),
  reasoningOutputEurPerMillion: nonNegativeNumberSchema.nullable().optional(),
  status: usagePricingModelStatusSchema.optional(),
});

export const usageTopConversationItemSchema = conversationUsageSchema.extend({
  costEur: nonNegativeNumberSchema,
});

export const usageTopConversationsSchema = z.object({
  items: z.array(usageTopConversationItemSchema),
});

export const companyUsageTopConversationItemSchema = conversationUsageSchema.omit({
  costEur: true,
});

export const companyUsageTopConversationsSchema = z.object({
  items: z.array(companyUsageTopConversationItemSchema),
});

export const usageTopUserItemSchema = userUsageOverviewSchema.extend({
  costEur: nonNegativeNumberSchema,
});

export const usageTopUsersSchema = z.object({
  items: z.array(usageTopUserItemSchema),
});

export const companyUsageMemberRowSchema = z.object({
  email: z.email({
    error: 'L’adresse email du membre est invalide.',
  }),
  eventsCount: nonNegativeIntSchema,
  lastEventAt: isoDateTimeSchema.nullable(),
  name: z.string().trim().min(1, {
    error: 'Le nom du membre est requis.',
  }),
  totals: usageTotalsSchema,
  userId: z.string().trim().min(1, {
    error: 'L’identifiant utilisateur est requis.',
  }),
});

export const companyUsageMemberDetailSchema = companyUsageMemberRowSchema.extend({
  byOperation: z.array(usageBreakdownItemSchema),
  byType: z.array(usageBreakdownItemSchema),
  conversationCount: nonNegativeIntSchema,
  messageCount: nonNegativeIntSchema,
});

export const companyUsageEventSchema = usageLogSchema.omit({
  costEur: true,
  pricingSource: true,
  pricingVersion: true,
});

const paginationMetaSchema = z.object({
  page: nonNegativeIntSchema.min(1),
  pageCount: nonNegativeIntSchema.min(1),
  pageSize: nonNegativeIntSchema.min(1),
  total: nonNegativeIntSchema,
});

export const paginatedUsageEventsSchema = z.object({
  items: z.array(usageLogSchema),
  meta: paginationMetaSchema,
});

export const paginatedConversationUsageSchema = z.object({
  items: z.array(conversationUsageSchema),
  meta: paginationMetaSchema,
});

export const paginatedUserUsageSchema = z.object({
  items: z.array(userUsageOverviewSchema),
  meta: paginationMetaSchema,
});

export const paginatedCompanyUsageMembersSchema = z.object({
  items: z.array(companyUsageMemberRowSchema),
  meta: paginationMetaSchema,
});

export const paginatedCompanyUsageEventsSchema = z.object({
  items: z.array(companyUsageEventSchema),
  meta: paginationMetaSchema,
});

export type UsageType = z.infer<typeof usageTypeSchema>;
export type UsageSource = z.infer<typeof usageSourceSchema>;
export type UsageStatus = z.infer<typeof usageStatusSchema>;
export type UsageOperation = z.infer<typeof usageOperationSchema>;
export type UsageMeasurementSource = z.infer<typeof usageMeasurementSourceSchema>;
export type UsageTotalsDTO = z.infer<typeof usageTotalsSchema>;
export type UsageBreakdownItemDTO = z.infer<typeof usageBreakdownItemSchema>;
export type UsageLogDTO = z.infer<typeof usageLogSchema>;
export type UsageSummaryDTO = z.infer<typeof usageSummarySchema>;
export type GlobalUsageSummaryDTO = z.infer<typeof globalUsageSummarySchema>;
export type CompanyUsageSummaryDTO = z.infer<typeof companyUsageSummarySchema>;
export type ConversationUsageDTO = z.infer<typeof conversationUsageSchema>;
export type UserUsageOverviewDTO = z.infer<typeof userUsageOverviewSchema>;
export type PaginatedUsageEventsDTO = z.infer<typeof paginatedUsageEventsSchema>;
export type PaginatedConversationUsageDTO = z.infer<typeof paginatedConversationUsageSchema>;
export type PaginatedUserUsageDTO = z.infer<typeof paginatedUserUsageSchema>;
export type UsageCostSummaryDTO = z.infer<typeof usageCostSummarySchema>;
export type UsageCostTimeSeriesGranularity = z.infer<typeof usageCostTimeSeriesGranularitySchema>;
export type UsageCostTimeSeriesBucketDTO = z.infer<typeof usageCostTimeSeriesBucketSchema>;
export type UsageCostTimeSeriesDTO = z.infer<typeof usageCostTimeSeriesSchema>;
export type CompanyUsageTimeseriesBucketDTO = z.infer<typeof companyUsageTimeseriesBucketSchema>;
export type CompanyUsageTimeseriesDTO = z.infer<typeof companyUsageTimeseriesSchema>;
export type UsageCostByModelItemDTO = z.infer<typeof usageCostByModelItemSchema>;
export type UsageCostByModelDTO = z.infer<typeof usageCostByModelSchema>;
export type CompanyUsageByModelItemDTO = z.infer<typeof companyUsageByModelItemSchema>;
export type CompanyUsageByModelDTO = z.infer<typeof companyUsageByModelSchema>;
export type CompanyUsageSummaryQuery = z.infer<typeof companyUsageSummaryQuerySchema>;
export type CompanyUsageTopConversationsQuery = z.infer<typeof companyUsageTopConversationsQuerySchema>;
export type CompanyUsageMembersQuery = z.infer<typeof companyUsageMembersQuerySchema>;
export type CompanyUsageEventsQuery = z.infer<typeof companyUsageEventsQuerySchema>;
export type UsagePricingModelStatus = z.infer<typeof usagePricingModelStatusSchema>;
export type UsagePricingModelDTO = z.infer<typeof usagePricingModelSchema>;
export type UsagePricingModelsDTO = z.infer<typeof usagePricingModelsSchema>;
export type UsagePricingModelUpsertDTO = z.infer<typeof usagePricingModelUpsertSchema>;
export type UsageTopConversationItemDTO = z.infer<typeof usageTopConversationItemSchema>;
export type UsageTopConversationsDTO = z.infer<typeof usageTopConversationsSchema>;
export type CompanyUsageTopConversationItemDTO = z.infer<typeof companyUsageTopConversationItemSchema>;
export type CompanyUsageTopConversationsDTO = z.infer<typeof companyUsageTopConversationsSchema>;
export type UsageTopUserItemDTO = z.infer<typeof usageTopUserItemSchema>;
export type UsageTopUsersDTO = z.infer<typeof usageTopUsersSchema>;
export type CompanyUsageMemberRowDTO = z.infer<typeof companyUsageMemberRowSchema>;
export type PaginatedCompanyUsageMembersDTO = z.infer<typeof paginatedCompanyUsageMembersSchema>;
export type CompanyUsageMemberDetailDTO = z.infer<typeof companyUsageMemberDetailSchema>;
export type CompanyUsageEventDTO = z.infer<typeof companyUsageEventSchema>;
export type PaginatedCompanyUsageEventsDTO = z.infer<typeof paginatedCompanyUsageEventsSchema>;
